import { useMixerMenuItem, useTunerMenuItem } from '@tunasong/audio-ui'
import { ElectricGuitar, Fretboard, MusicNote, OpenLink } from '@tunasong/icons'
import { useKeyboardMenuItem } from '@tunasong/music-ui'
import type { Entity, Persisted } from '@tunasong/schemas'
import {
  Typography,
  type TypographyProps,
  useNavigate,
  useUserConfig,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Disclosure,
  DisclosureTrigger,
  DisclosureContent,
} from '@tunasong/ui-lib'
import { type FC } from 'react'
import { musicTheoryTools } from './music-theory-tools.js'
import styles from './tools-menu-items.module.css'

export interface ToolsMenuItemsProps {
  entity?: Persisted<Entity>
  variant?: TypographyProps['variant']
  onClose?: () => void
}

/**
 * The Tools navigation list (sidebar and the mobile Tools dialog). A plain list of buttons, not an
 * ARIA menu: every row is a Tab stop and the current page is announced with aria-current.
 */
export const ToolsMenuItems: FC<ToolsMenuItemsProps> = props => {
  const { onClose, variant } = props

  const { dialog: mixerDialog, menuItem: mixerMenuItem } = useMixerMenuItem({
    variant,
    onClose,
  })
  const { dialog: tunerDialog, menuItem: tunerMenuItem } = useTunerMenuItem({ variant, onClose })
  const { dialog: keyboardDialog, menuItem: keyboardMenuItem } = useKeyboardMenuItem({
    variant,
    onClose,
  })

  const navigate = useNavigate()
  const [externalTools = []] = useUserConfig('externalTools')

  const currentFor = (route: string) => (location.pathname === route ? 'page' : undefined)

  return (
    <>
      <List disablePadding className={styles.tools}>
        <li>{mixerMenuItem}</li>
        <li>{tunerMenuItem}</li>
        <li>{keyboardMenuItem}</li>

        <li>
          <ListItemButton
            aria-current={currentFor('/app/guitarchords')}
            onClick={() => void navigate('/app/guitarchords')}
          >
            <ListItemIcon className={styles.listItemIcon}>
              <ElectricGuitar />
            </ListItemIcon>
            <ListItemText>
              <Typography variant={variant}>Guitar Chords</Typography>
            </ListItemText>
          </ListItemButton>
        </li>
        <li>
          <ListItemButton
            aria-current={currentFor('/app/guitarpatterns')}
            onClick={() => void navigate('/app/guitarpatterns')}
          >
            <ListItemIcon className={styles.listItemIcon}>
              <Fretboard />
            </ListItemIcon>
            <ListItemText>
              <Typography variant={variant}>Guitar Patterns</Typography>
            </ListItemText>
          </ListItemButton>
        </li>
        <Disclosure groupId="sidebar.music-theory" defaultOpen>
          <DisclosureTrigger>
            <ListItemIcon className={styles.listItemIcon}>
              <MusicNote />
            </ListItemIcon>
            <ListItemText>
              <Typography variant={variant}>Music Theory</Typography>
            </ListItemText>
          </DisclosureTrigger>
          <DisclosureContent>
            {musicTheoryTools.map(({ label, route, Icon }) => (
              <li key={route}>
                <ListItemButton aria-current={currentFor(route)} onClick={() => void navigate(route)}>
                  <ListItemIcon className={styles.listItemIcon}>
                    <Icon />
                  </ListItemIcon>
                  <ListItemText>
                    <Typography variant={variant}>{label}</Typography>
                  </ListItemText>
                </ListItemButton>
              </li>
            ))}
          </DisclosureContent>
        </Disclosure>

        {externalTools.length > 0 ? (
          <li>
            <Typography variant="caption">External Tools</Typography>
          </li>
        ) : null}
        {externalTools.map((tool, index) => (
          <li key={index}>
            <ListItemButton
              onClick={() => {
                void navigate(
                  `/app/external-tool?url=${encodeURIComponent(tool.url)}&name=${encodeURIComponent(tool.name)}`
                )
                onClose?.()
              }}
            >
              <ListItemIcon className={styles.listItemIcon}>
                <OpenLink />
              </ListItemIcon>
              <ListItemText>
                <Typography variant={variant}>{tool.name}</Typography>
              </ListItemText>
            </ListItemButton>
          </li>
        ))}
      </List>

      {mixerDialog}
      {tunerDialog}
      {keyboardDialog}
    </>
  )
}
